import React from 'react';
import AtableLogo from '../shared/design/components/AtableLogo';
import { Button } from '../shared/design';
import { firestoreService } from '../shared/services/firestoreService';
import { USE_FIREBASE } from '../shared/services/apiConfig';
import { apiService } from '../shared/services/api';
import { SmartphoneIcon } from '../restaurateur/components/icons/UiIcons';
import { APP_STORE_URL, PLAY_STORE_URL } from '../guest/storeConfig';
import { storeLinks } from '../guest/guestPage';

// Liens des stores : les mêmes que la page /t (src/guest/storeConfig.ts, vérifiés par
// storeLinks : https vers apps.apple.com / play.google.com seulement). Vide : « Bientôt disponible ».
const STORE_LINKS = storeLinks({ appStoreUrl: APP_STORE_URL, playStoreUrl: PLAY_STORE_URL }, 'other');

/**
 * Affiché aux utilisateurs avec role=CLIENT qui se connectent sur l'app web.
 * L'expérience client est désormais réservée à l'app mobile (iOS / Android).
 * `onRequestRestaurateurAccess` : lien « Demander un accès professionnel »
 * (absent en mode démo).
 */
const ClientMobileRedirect: React.FC<{ onRequestRestaurateurAccess?: () => void }> = ({ onRequestRestaurateurAccess }) => {
    const handleLogout = async () => {
        if (USE_FIREBASE) {
            await firestoreService.logout();
        } else {
            await apiService.logout();
        }
        window.location.reload();
    };

    return (
        <div className="min-h-screen bg-cream dark:bg-gray-950 flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                <div className="flex justify-center mb-8">
                    <AtableLogo variant="lockup" height={120} />
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-cream-200 dark:border-gray-800 shadow-md p-8 space-y-6">
                    <div className="w-16 h-16 mx-auto rounded-full bg-brand-primary/10 dark:bg-brand-primary/20 text-brand-primary flex items-center justify-center">
                        <SmartphoneIcon className="w-8 h-8" />
                    </div>

                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                            L'app atable est sur mobile
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                            Votre programme de fidélité, vos points et vos récompenses
                            sont disponibles dans l'application mobile atable.
                        </p>
                    </div>

                    <div className="flex flex-col gap-3">
                        {STORE_LINKS.map((link) => link.href ? (
                            <a
                                key={link.store}
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                            >
                                Télécharger sur {link.store === 'app_store' ? "l'App Store" : link.label}
                            </a>
                        ) : (
                            <span
                                key={link.store}
                                aria-disabled="true"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-xl font-semibold cursor-default"
                            >
                                {link.label} — Bientôt disponible
                            </span>
                        ))}
                    </div>

                    <div className="pt-4 border-t border-cream-200 dark:border-gray-800">
                        <Button variant="ghost" size="sm" onClick={handleLogout}>
                            Se déconnecter
                        </Button>
                    </div>
                </div>

                {onRequestRestaurateurAccess && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-6">
                        Vous êtes restaurateur ?{' '}
                        <button
                            type="button"
                            onClick={onRequestRestaurateurAccess}
                            className="font-semibold text-brand-600 dark:text-brand-300 underline hover:text-brand-700"
                        >
                            Demander un accès professionnel
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
};

export default ClientMobileRedirect;
